import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import PaymentForm from "components/stripe/payment-form";
import CompletePayment from "components/stripe/payment-complete";
import OrderSummary from "components/stripe/order-summary";
import {
  offers,
  appearance,
  loader,
  stripePromise,
  PAYMENT_RETRIEVE_ERROR,
} from "constants/stripe";
import { useLocation } from "react-router-dom";
import { useMutationPaymentIntent } from "mutations/useMutationPaymentIntent";
import { Offer } from "types/Stripe.type";
import PAGES from "routes/constants";
import { getAdemeNumber, setAdemeNumber, clearPromoCode as clearStoredPromoCode } from "services/storages/userStorage";
import LogoMobile from "assets/images/logo-checkdpe-mobile.png";
import { ReactComponent as IconArrow } from "assets/images/arrow.svg";

import "styles/stripe.scss";

function StripePaymentPage() {
  const [clientSecret, setClientSecret] = useState("");
  const location = useLocation();
  const isPaymentComplete = location.pathname.includes(PAGES.paymentComplete);
  const [orderData, setOrderData] = useState<Offer | undefined>(
    !isPaymentComplete ? offers[location.state?.offerId] : undefined
  );
  const oldPrice = useRef(0);
  const isUsePromoCode = useRef(false);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("route:stripe-payment:enter", {
        detail: { pathname: window.location.pathname },
      })
    );

    // Clear any previously stored promo code so the input starts blank
    clearStoredPromoCode();

    setOrderData((prev) => {
      if (!prev) {
        return prev;
      }
      const cleared = _.cloneDeep(prev);
      _.set(cleared, "promoCode", "");
      return cleared;
    });

  }, []);

  useEffect(() => {
    if (!_.isEmpty(orderData)) {
      const newOrderData = _.cloneDeep(orderData);
      _.set(newOrderData, "refAdeme", location.state?.refAdeme);
      _.set(newOrderData, "address", location.state?.address);

      setOrderData(newOrderData);
      if (!isUsePromoCode.current) {
        oldPrice.current = newOrderData.price;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (!getAdemeNumber()) {
    setAdemeNumber(location.state?.refAdeme);
  }
  const {
    data: stripeData,
    mutate,
    isSuccess: stripeSuccess,
    error: stripeError,
  } = useMutationPaymentIntent();
  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const showSnackbar = (message: string, type: "success" | "error") => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const handleAddPromoCode = (promoCode: string, discountPercent: number) => {
    setOrderData((prev) => {
      if (!prev) {
        return prev;
      }
      const newOrderData = _.cloneDeep(prev);
      isUsePromoCode.current = true;
      _.set(newOrderData, "promoCode", promoCode);
      // always computed from the undiscounted price: applying a code changes the
      // clientSecret, which remounts PaymentForm, so the discount cannot be derived
      // from whatever price is on screen at that moment
      _.set(
        newOrderData,
        "price",
        +((oldPrice.current * (100 - discountPercent)) / 100).toFixed(2)
      );
      return newOrderData;
    });
  };

  const handleRemovePromoCode = () => {
    setOrderData((prev) => {
      if (!prev) {
        return prev;
      }
      const newOrderData = _.cloneDeep(prev);
      if (newOrderData.price === oldPrice.current && !isUsePromoCode.current) {
        return prev;
      }
      _.set(newOrderData, "promoCode", "");
      _.set(newOrderData, "price", oldPrice.current);
      isUsePromoCode.current = false;
      return newOrderData;
    });
  };

  useEffect(() => {
    if (
      !isPaymentComplete &&
      orderData &&
      orderData?.address &&
      orderData?.refAdeme
    ) {
      mutate(orderData);
    }
  }, [isPaymentComplete, mutate, orderData]);

  useEffect(() => {
    if (stripeSuccess && stripeData?.client_secret) {
      setClientSecret(stripeData.client_secret);
    }
    if (stripeError) {
      showSnackbar(PAYMENT_RETRIEVE_ERROR, "error");
    }
  }, [snackbar, stripeData, stripeSuccess, stripeError]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlClientSecret = searchParams.get("payment_intent_client_secret");
    setClientSecret(urlClientSecret || "");
  }, [isPaymentComplete]);

  const descriptionItems = orderData?.description
    .split("\n")
    .map((item: string) => item.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-100 py-12 text-center">
      <div className="inline-flex relative gap-x-5">
      {!isPaymentComplete && (<div className="absolute top-0 right-full mr-6 border-2 border-[#619ea0] rounded-xl p-4 w-[350px] hidden xl:block">
          <img src={LogoMobile} alt="logo" className="w-[80px] mx-auto mb-2" />
          <h5 className="text-[#7c7c7c] font-bold text-center">
            Les prochaines étapes
          </h5>

          <ul className="ml-4 mt-4 text-base text-[#7c7c7c] text-left">
            <li className="flex items-start gap-x-1">
              <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
              <span>
                Saisie des <b>informations</b> nécessaires à la
                finalisation de votre commande (mail, moyen de paiement)
              </span>
            </li>
            <li className="flex items-start gap-x-1">
              <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
              <span>Validation du paiement auprès de votre banque</span>
            </li>
            <li className="flex items-start gap-x-1">
              <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
              <span>
                Activation de votre compte
              </span>
            </li>
            <li className="flex items-start gap-x-1">
              <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
              <span>
                Accès à votre <b>tableau de bord du DPE</b> avec le <b>score de fiabilité</b> et les anomalies
              </span>
            </li>
            <li className="flex items-start gap-x-1">
              <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
              <span>
                Un <b>rapport de fiabilité au format pdf</b>, vous sera envoyé
                dans les 24h
              </span>
            </li>
            {descriptionItems &&
              descriptionItems[
                descriptionItems.length - 1
              ]?.toLowerCase().includes("proposition de travaux") && (
                <li className="flex items-start gap-x-1">
                  <IconArrow className="w-3 h-3 fill-[#15585a] relative top-1 flex-shrink-0" />
                  <span>
                    Accès à une <b>proposition de travaux</b> adaptée à votre projet
                  </span>
                </li>
              )}
          </ul>

          <p className="text-center text-lg mt-5 text-[#7c7c7c]">
            Une question ? Un problème ?
          </p>
          <p className="text-center text-lg mt-2 text-[#7c7c7c]">
            Contactez-nous :{" "}
            <a
              href="mailto:support@checkdpe.fr"
              className="!text-[#7c7c7c] font-normal"
            >
              support@checkdpe.fr
            </a>
          </p>
        </div>)}

        <div className="max-w-md relative">
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
            {isPaymentComplete ? "" : "Finaliser la commande"}
          </h1>

          {!isPaymentComplete && orderData && (
            <OrderSummary
              name={orderData.name}
              price={orderData.price}
              description={orderData.description}
              refAdeme={orderData.refAdeme}
              address={orderData.address}
            />
          )}

          {clientSecret && (
            <Elements
              key={clientSecret}
              options={{ clientSecret, appearance, loader }}
              stripe={stripePromise}
            >
              {isPaymentComplete ? (
                <CompletePayment clientSecret={clientSecret} />
              ) : (
                <PaymentForm
                  amount={orderData?.price}
                  appliedPromoCode={orderData?.promoCode}
                  onAddPromoCode={handleAddPromoCode}
                  onRemovePromoCode={handleRemovePromoCode}
                  clientSecret={clientSecret}
                  offerName={orderData?.name}
                />
              )}
            </Elements>
          )}
        </div>

        {!isPaymentComplete && (<div className="absolute top-0 left-full ml-6 border-2 border-[#619ea0] rounded-xl p-4 w-[350px] hidden xl:block">
          <img
            src="https://checkdpe.fr/wp-content/uploads/2025/09/rapport-checkDPE.webp"
            alt="exemple de dashboard"
            className="opacity-70"
          />
        </div>)}
      </div>
    </div>
  );
}

export default StripePaymentPage;
